/**
 * Spec Phase 4 Slice 4E — FeatureMapDock.
 * Phase 8 Slice 8D — drag-to-anchor + unlink.
 *
 * Lists feature mappings (feature_tag → anchor_paragraph_id).
 *  - Unanchored rows are HTML5-draggable; the dataTransfer
 *    payload is the mapping id under the custom MIME type
 *    'application/x-spec-feature'. ParagraphBlock listens for
 *    that MIME on drop.
 *  - Anchored rows show their anchor pill plus a small unlink
 *    icon that PATCHes anchor_paragraph_id → null and flips
 *    the row back to draggable.
 *
 * Drag direction is one-way (dock → paragraph) per brief §10.
 */
import React, { useCallback, useState } from 'react';
import { Anchor, Link2Off, Loader2, RefreshCw } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import { postSpecExtractFeatures } from '@/api/client';
import Dock from './Dock';

export const FEATURE_DRAG_MIME = 'application/x-spec-feature';

export default function FeatureMapDock(): JSX.Element {
  const featureMappings = useSpecStore((s) => s.featureMappings);
  const sections = useSpecStore((s) => s.sections);
  const paragraphsBySection = useSpecStore((s) => s.paragraphsBySection);
  const unanchorFeature = useSpecStore((s) => s.unanchorFeature);
  const showToast = useSpecStore((s) => s.showToast);
  const matterId = useSpecStore((s) => s.matterId);

  // BUG-042 — manual re-extract trigger. The freeze-time auto-call
  // silently failed for some matters (BUG-014 boolean coercion);
  // this lets the drafter recover without a backend intervention.
  const [extracting, setExtracting] = useState(false);
  const onReExtract = useCallback(async () => {
    if (!matterId || extracting) return;
    setExtracting(true);
    try {
      const r = await postSpecExtractFeatures(matterId);
      // The endpoint returns counts but not the rows; refetch the
      // mappings list so the panel re-renders with the new entries.
      const mods = await import('@/api/client');
      const fresh = await mods.getSpecFeatureMappings(matterId);
      if (Array.isArray(fresh)) {
        useSpecStore.setState({ featureMappings: fresh });
      }
      showToast(
        r.inserted > 0
          ? `Extracted ${r.inserted} new feature${r.inserted === 1 ? '' : 's'} (${r.total} total).`
          : (r.total > 0
            ? `No new features (${r.total} already extracted).`
            : 'No frozen claims to extract from yet.'),
      );
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? 'Extract failed.';
      showToast(`Re-extract failed: ${msg}`);
    } finally {
      setExtracting(false);
    }
  }, [matterId, extracting, showToast]);

  // Reverse-index every paragraph by id so the row's anchor pill
  // can show "→ [0007]" without an extra round-trip.
  const paraNumberById: Record<string, string> = {};
  for (const sec of sections) {
    const list = paragraphsBySection[sec.id] ?? [];
    for (const p of list) paraNumberById[p.id] = p.paragraph_number;
  }

  const anchoredCount = featureMappings.filter(
    (f) => f.anchor_paragraph_id !== null,
  ).length;

  return (
    <Dock
      dockKey="featureMap"
      icon={<Anchor className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Feature → Spec map"
      pillCount={featureMappings.length}
      meta={
        featureMappings.length === 0
          ? 'Features auto-extract on claims freeze. Drag a row onto a paragraph to anchor.'
          : `${anchoredCount}/${featureMappings.length} anchored`
      }
      testId="spec-dock-feature-map"
    >
      {/* BUG-042 — empty-state recovery button. Visible when no
          feature mappings exist (the freeze-time auto-extract
          may have silently failed); clicking re-runs the
          extraction. Idempotent on re-run so we leave the
          button visible even when count > 0 — drafter may want
          to re-extract after freezing more claims. */}
      {featureMappings.length === 0 && (
        <div
          className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-2 py-3 text-center"
          data-testid="spec-feature-empty"
        >
          <p className="mb-2 text-[11px] text-zinc-600">
            No features extracted from frozen claims yet.
          </p>
          <button
            type="button"
            disabled={extracting || !matterId}
            onClick={onReExtract}
            className="inline-flex h-7 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="spec-feature-extract-trigger"
          >
            {extracting
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RefreshCw className="h-3 w-3" />}
            Extract from frozen claims
          </button>
        </div>
      )}
      {featureMappings.length === 0 ? null : (
        <ul className="space-y-1">
          {featureMappings.map((f) => {
            const isAnchored = f.anchor_paragraph_id !== null;
            const anchorParaNum = isAnchored
              ? (paraNumberById[f.anchor_paragraph_id!] ?? '????')
              : null;
            return (
              <li
                key={f.id}
                className={[
                  'group flex items-start gap-2 rounded-sm bg-white px-2 py-1',
                  isAnchored
                    ? ''
                    : 'cursor-grab focus:outline focus:outline-2 focus:outline-blue-400',
                ].join(' ')}
                data-testid={`spec-feature-row-${f.feature_tag}`}
                draggable={!isAnchored}
                // Phase 12G — a11y. aria-grabbed flips during the
                // browser drag op so screen readers can announce
                // the start/end of the drag. Anchored rows are
                // not draggable; they show the "→ [paraNum]" pill
                // instead. Keyboard alt: Enter / Space dispatches
                // a 'spec.feature.anchor-request' bus event that
                // the active paragraph picks up (no-op when there
                // is no active paragraph; user gets a toast).
                tabIndex={isAnchored ? -1 : 0}
                role={isAnchored ? undefined : 'button'}
                aria-grabbed={isAnchored ? undefined : false}
                aria-label={
                  isAnchored
                    ? `Feature ${f.feature_tag}: anchored to ${anchorParaNum}`
                    : `Feature ${f.feature_tag}: drag onto a paragraph or press Enter to anchor at the active paragraph`
                }
                onDragStart={(e) => {
                  if (isAnchored) {
                    e.preventDefault();
                    return;
                  }
                  e.currentTarget.setAttribute('aria-grabbed', 'true');
                  e.dataTransfer.setData(FEATURE_DRAG_MIME, f.id);
                  e.dataTransfer.effectAllowed = 'link';
                }}
                onDragEnd={(e) => {
                  if (!isAnchored) {
                    e.currentTarget.setAttribute('aria-grabbed', 'false');
                  }
                }}
                onKeyDown={(e) => {
                  if (isAnchored) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent(
                      'spec.feature.anchor-request',
                      { detail: { feature_id: f.id, feature_tag: f.feature_tag } },
                    ));
                  }
                }}
              >
                <span className="font-mono text-[10px] font-medium text-zinc-600">
                  {f.feature_tag}
                </span>
                <span className="flex-1 text-[11px] text-zinc-700 line-clamp-2">
                  {f.feature_text}
                </span>
                {isAnchored ? (
                  <span
                    className="shrink-0 rounded-sm bg-emerald-50 px-1 font-mono text-[9px] text-emerald-700"
                    data-testid={`spec-feature-anchor-pill-${f.feature_tag}`}
                  >
                    → [{anchorParaNum}]
                  </span>
                ) : (
                  <span className="shrink-0 rounded-sm bg-zinc-100 px-1 font-mono text-[9px] text-zinc-500">
                    unanchored
                  </span>
                )}
                {isAnchored && (
                  <button
                    type="button"
                    className="shrink-0 rounded-sm p-0.5 text-zinc-300 opacity-0 hover:bg-zinc-100 hover:text-zinc-700 group-hover:opacity-100"
                    onClick={async () => {
                      const r = await unanchorFeature(f.id);
                      if (r.kind === 'ok') {
                        showToast(
                          `Feature ${r.featureTag ?? f.feature_tag} unanchored`,
                        );
                      } else {
                        showToast(`Unanchor failed: ${r.message ?? ''}`);
                      }
                    }}
                    aria-label="Unanchor feature"
                    title="Unanchor (drag again to re-anchor)"
                    data-testid={`spec-feature-unanchor-${f.feature_tag}`}
                  >
                    <Link2Off className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Dock>
  );
}
